import { useEffect, useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

const AdminSupport = () => {
  const { api } = useAdminAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/tickets"); setTickets(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Reply cannot be empty");
    setBusy(true);
    try { await api.post(`/admin/tickets/${selected.ticket_id}/reply`, { message: reply }); toast.success("Reply sent"); setReply(""); await load(); const fresh = (await api.get("/admin/tickets")).data.find((t) => t.ticket_id === selected.ticket_id); setSelected(fresh); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    setBusy(true);
    try { await api.post(`/admin/tickets/${selected.ticket_id}/resolve`); toast.success("Ticket resolved"); setSelected(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-support-page">
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Support</span>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Support inbox</h1>
        <p className="mt-1 text-sm text-[#0D1B2A]/60">Tickets from creators and backers.</p>
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-[#0D1B2A]/10 overflow-hidden">
        {loading ? <div className="p-12 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#0D1B2A]/60" /></div> :
          tickets.length === 0 ? <div className="p-12 text-center text-[#0D1B2A]/60">Inbox is empty.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFFBF5] text-[#0D1B2A]/50 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">Sender</th>
                  <th className="text-left px-5 py-3">Subject</th>
                  <th className="text-left px-5 py-3">Type</th>
                  <th className="text-left px-5 py-3">Submitted</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0D1B2A]/5">
                {tickets.map((t) => (
                  <tr key={t.ticket_id} onClick={() => setSelected(t)} data-testid={`ticket-row-${t.ticket_id}`} className="cursor-pointer hover:bg-[#FFFBF5]/60">
                    <td className="px-5 py-3">
                      <div className="font-medium">{t.user_name}</div>
                      <div className="text-xs text-[#0D1B2A]/60">{t.user_email}</div>
                    </td>
                    <td className="px-5 py-3 font-medium truncate max-w-md">{t.subject}</td>
                    <td className="px-5 py-3 text-[#0D1B2A]/80">{t.sender_type}</td>
                    <td className="px-5 py-3 text-xs text-[#0D1B2A]/60">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs rounded-full px-2 py-0.5 ${t.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{t.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-[640px] rounded-2xl max-h-[90vh] overflow-auto" data-testid="ticket-modal">
          <DialogHeader><DialogTitle className="font-display">{selected?.subject}</DialogTitle></DialogHeader>
          <div className="text-xs text-[#0D1B2A]/60">From <strong>{selected?.user_name}</strong> ({selected?.sender_type})</div>
          <div className="mt-3 space-y-3">
            {selected?.messages?.map((m, i) => (
              <div key={i} className={`rounded-2xl p-4 border ${m.from === "admin" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-[#FFFBF5] border-[#0D1B2A]/10"}`}>
                <div className={`text-[10px] uppercase tracking-widest mb-1 ${m.from === "admin" ? "text-[#E85D04]" : "text-[#0D1B2A]/50"}`}>{m.from === "admin" ? "Admin" : "User"} · {new Date(m.at).toLocaleString()}</div>
                <div className="text-sm whitespace-pre-wrap">{m.message}</div>
              </div>
            ))}
          </div>
          {selected?.status !== "resolved" && (
            <>
              <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply…" data-testid="ticket-reply-input"
                className="rounded-xl border-[#0D1B2A]/15 mt-4" />
              <div className="flex justify-end gap-2">
                <button onClick={resolve} disabled={busy} data-testid="ticket-resolve-btn"
                  className="inline-flex items-center gap-1 rounded-xl border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-4 py-2 text-sm font-semibold disabled:opacity-60">
                  <CheckCircle2 className="h-4 w-4" /> Mark resolved
                </button>
                <button onClick={sendReply} disabled={busy || !reply.trim()} data-testid="ticket-reply-btn"
                  className="inline-flex items-center gap-1 rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send reply</>}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupport;
